import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { Clock, Save } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const BusinessInfoEditor = () => {
  const [businessHours, setBusinessHours] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadBusinessInfo();
  }, []);

  const loadBusinessInfo = async () => {
    try {
      const token = sessionStorage.getItem('token');
      const response = await axios.get(`${API_URL}/business-info/business_hours`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBusinessHours(response.data.info_value || '');
    } catch (error) {
      console.error('Load business info error:', error);
      // If not found, use default
      setBusinessHours('Monday - Friday: 9:00 AM - 5:00 PM\nSaturday: 10:00 AM - 3:00 PM\nSunday: Closed\n\nHolidays: Closed on major holidays');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = sessionStorage.getItem('token');
      await axios.put(
        `${API_URL}/business-info/business_hours`,
        { value: businessHours },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Business hours updated successfully!');
    } catch (error) {
      console.error('Save business info error:', error);
      const errorMsg = error.response?.data?.error || error.message || 'Failed to save business hours';
      toast.error(`Error: ${errorMsg}. Make sure you've run create_business_info_table.sql`);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="flex items-center space-x-3 mb-4">
        <Clock className="text-primary-600" size={24} />
        <h2 className="text-2xl font-bold">Business Hours (Homepage Display)</h2>
      </div>

      <p className="text-gray-600 mb-6">
        This information will be displayed on the homepage for all users (clients, employees, and admins).
        Edit the text below to show your business hours, holidays, and any special notes.
      </p>

      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Business Hours Information
        </label>
        <textarea
          value={businessHours}
          onChange={(e) => setBusinessHours(e.target.value)}
          rows={8}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono text-sm"
          placeholder="Enter your business hours, holidays, and any special notes..."
        />
        <p className="text-sm text-gray-500 mt-2">
          Tip: Use line breaks to format your hours nicely. Example:<br/>
          Monday - Friday: 9:00 AM - 5:00 PM<br/>
          Saturday: 10:00 AM - 3:00 PM<br/>
          Sunday: Closed
        </p>
      </div>

      <div className="flex items-center justify-between p-4 bg-blue-50 border border-blue-200 rounded-lg mb-4">
        <div>
          <h3 className="font-semibold text-blue-900">Preview</h3>
          <div className="text-sm text-blue-800 mt-2 whitespace-pre-line">
            {businessHours || 'No business hours set'}
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving}
          className="btn btn-primary flex items-center space-x-2"
        >
          <Save size={18} />
          <span>{saving ? 'Saving...' : 'Save Business Hours'}</span>
        </button>
      </div>
    </div>
  );
};

export default BusinessInfoEditor;
